import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, useStripe } from '@stripe/react-stripe-js';

import {
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Modal,
  Radio,
  RadioGroup,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { COLOR_GREY_400, COLOR_GREY_600, COLOR_PRIMARY_1, COLOR_PRIMARY_2, COLOR_TEXT_GREY } from 'src/colors';
import {
  COLOR_GREEN,
  COLOR_GREY,
  CountryCodeWithCurrency,
  CurrencyWithSubscriptionPrice,
  ProductIDOptions,
  exchangeRates,
} from 'src/const';
import {
  confirmCardPaymentRecursively,
  formatCurrencyBasedOnCountryCode,
  getPercentage,
} from 'src/utils/functions';
import { callApi } from 'src/core/callApi';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Payment } from 'src/App/HOC';
import postOnSlack from 'src/core/postOnSlack';
import { logAmplitudeEvent } from 'src/utils/analytics';

const StripeContext = ({ plan, handleClose }) => {
  const theme = useTheme();
  const stripe = useStripe();
  const matches = useMediaQuery(theme.breakpoints.down(900));
  const userSignupCountryCode = useSelector((state: any) => state.dashboard.user.signupCountryCode);
  const userEmail = useSelector((state: any) => state.dashboard.user.email);
  const search = useSelector((state: any) => state.dashboard.search);
  const userLocalCurrency = CountryCodeWithCurrency[userSignupCountryCode] || 'USD';
  const subscriptionPriceAccordingToUserLocalCurrency =
    CurrencyWithSubscriptionPrice[userLocalCurrency];
  const [subscriptionUpgradeLoading, setSubscriptionUpgradeLoading] = useState(false);
  const duration = 'yearly';
  const [showPaymentCard, setShowPaymentCard] = useState(false);

  const [finalDueAmount, setFinalDueAmount] = useState<number | null>(null);

  useEffect(() => {
    logAmplitudeEvent('yearly_upgrade_view_from_cancellation', {
      plan_name: plan,
      plan_duration: duration,
      is_upgrade: true,
      view: '',
      visited_from: '',
    });
  }, [plan, duration]);

  const currencySymbol = exchangeRates[userLocalCurrency].symbol;

  const monthlyPrice = parseFloat(subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly']);
  const notDiscountedYearlyPrice = monthlyPrice * 12;
  const yearlyPrice = subscriptionPriceAccordingToUserLocalCurrency[plan]['yearly'];
  const monthlyDiscount = subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'];

  const totalYearlyDiscount = notDiscountedYearlyPrice - yearlyPrice + monthlyDiscount;
  const formattedTotalYearlyDiscount = formatCurrencyBasedOnCountryCode(
    totalYearlyDiscount,
    userLocalCurrency
  );

  const discountPercentage = Math.abs(
    getPercentage(notDiscountedYearlyPrice - totalYearlyDiscount, notDiscountedYearlyPrice)
  ).toFixed(0);

  const upgradePrice =
    subscriptionPriceAccordingToUserLocalCurrency[plan]['yearly'] -
    subscriptionPriceAccordingToUserLocalCurrency[plan]['monthly'];

  console.log("Upgrade Price = ", upgradePrice);

  const getFinalDueAmount = async () => {
    setFinalDueAmount(null);

    try {
      const url =
        'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
      const testProductID = 'price_1PQoO6BVoeq938b5NtkhWK5L';
      const config = {
        queryParams: {
          newProductID: ProductIDOptions[plan].productID[duration],
          // newProductID: testProductID,
          newProductName: plan,
          newDuration: duration,
          operation: 'fetch_yearly_discounted_price_from_cancellation',
          currency: userLocalCurrency,
          // isTest: true,
        },
      };
      const resp = await callApi(url, config, true);
      const dueAmount = resp.yearly_discounted_price_from_cancellation;
      if (dueAmount && !isNaN(Number(dueAmount))) {
        setFinalDueAmount(dueAmount);
      } else {
        setFinalDueAmount(upgradePrice);
      }
    } catch (err) {
      setFinalDueAmount(upgradePrice);
    }
  };

  useEffect(() => {
    getFinalDueAmount();
  }, [duration]);

  const upgradeSubscription = async () => {
    try {
      if (!stripe) {
        return;
      }
      setSubscriptionUpgradeLoading(true);
      const url =
        'https://9s2r54r64l.execute-api.us-east-2.amazonaws.com/default/upgradeStripeSubscription';
      const testProductID = 'price_1PQoO6BVoeq938b5NtkhWK5L';
      const config = {
        queryParams: {
          newProductID: ProductIDOptions[plan].productID[duration],
          // newProductID: testProductID,
          newProductName: plan,
          newDuration: duration,
          operation: 'modify_subscription',
          cancellation_discount: true,
          // isTest: true,
        },
      };
      const resp = await callApi(url, config, true);
      setSubscriptionUpgradeLoading(false);
      if (resp.subscription_modification_status == 'success') {
        logAmplitudeEvent('subscribed', {
          plan_name: plan,
          plan_duration: duration,
          is_upgrade: true,
        });
        handleClose();
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (resp.subscription_modification_status == 'requires_action') {
        // requires 3D auth
        const clientSecret = resp.client_secret;
        const isAdditionalActionSuccess = await confirmCardPaymentRecursively(stripe, clientSecret);
        if (!isAdditionalActionSuccess) {
          postOnSlack(
            'tech',
            `3D Authentication failed for ${userEmail} during upgrade attempt to ${plan}-${duration}`
          );
          setShowPaymentCard(true);
        } else {
          console.log('3D auth success. Re-fetch payment intent from lambda');
          return upgradeSubscription();
        }
      } else {
        setShowPaymentCard(true);
      }
    } catch (err) {
      setSubscriptionUpgradeLoading(false);
    }
  };

  return (
    <Grid
      container
      direction="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      sx={{
        background: 'white',
        width: matches ? '100vw' : '500px',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: 4,
        outline: 'none !important',
        padding: 3,
        height: 'auto',
        maxHeight: matches ? '100dvh' : '90vh',
        overflow: 'auto',
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            variant="h5"
            sx={{ color: COLOR_PRIMARY_2, fontWeight: 500, textTransform: 'capitalize' }}
          >
            Upgrade to Yearly Plan
          </Typography>
          <CloseIcon
            sx={{ color: COLOR_GREY_400, cursor: 'pointer', fontWeight: 'bold' }}
            onClick={handleClose}
          />
        </Grid>

        <Grid container item direction="row" justifyContent="flex-start" xs={12}>
          <Typography style={{ fontWeight: 'bold', marginTop: '20px' }}>Upgrade Summary</Typography>
        </Grid>

        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
          sx={{ marginBottom: '10px' }}
        >
          <Typography variant="body1">Plan</Typography>
          <Typography variant="body1">Yearly - {plan} Plan</Typography>
        </Grid>

        <Grid item xs={12} sx={{ margin: '10px 0', width: '100%' }}>
          <Divider />
        </Grid>

        <Grid
          item
          container
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          xs={12}
          sx={{ marginBottom: '10px' }}
        >
          <Typography variant="body2" sx={{color: COLOR_TEXT_GREY}}>
            <span
              style={{
                padding: '2px 4px',
                background: COLOR_GREEN + '44',
                color: COLOR_GREEN,
                marginRight: '5px',
                fontSize: '12px',
                borderRadius: '900px',
              }}
            >
              {discountPercentage}% off
            </span>
            Yearly Discount
          </Typography>
          <Typography variant="body2" sx={{color: COLOR_TEXT_GREY}}>
            -{currencySymbol}
            {formattedTotalYearlyDiscount}{' '}
          </Typography>
        </Grid>

        {!showPaymentCard ? (
          <>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              xs={12}
            >
              <Typography variant="body1" sx={{fontSize: '18px'}}>Total Amount Due Today</Typography>              
              {finalDueAmount ? (
                <Typography variant="body1" sx={{ fontWeight: 'bold', color: COLOR_PRIMARY_2, fontSize: '24px' }}>
                  {exchangeRates[userLocalCurrency].symbol}
                  {formatCurrencyBasedOnCountryCode(finalDueAmount, userLocalCurrency)}
                </Typography>
              ) : (
                <CircularProgress />
              )}
            </Grid>
            <Grid
              item
              container
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              xs={12}
              sx={{ marginTop: '25px' }}
            >
              <Button
                sx={{
                  background: COLOR_PRIMARY_2,
                  color: 'white',
                  width: '100%',
                  ':hover': {
                    background: COLOR_PRIMARY_1,
                    color: 'white',
                  },
                }}
                onClick={upgradeSubscription}
                disabled={subscriptionUpgradeLoading}
              >
                Confirm Changes {subscriptionUpgradeLoading && <CircularProgress />}
              </Button>
            </Grid>
          </>
        ) : (
          <Grid
            item
            container
            direction="row"
            justifyContent="center"
            alignItems="center"
            xs={12}
            sx={{ mt: 2 }}
          >
            <Grid
              container
              item
              sx={{
                border: `2px solid ${COLOR_GREY}`,
                borderRadius: 10,
                padding: theme.spacing(2, 3, 2),
                boxShadow: theme.shadows[5],
              }}
            >
              <Payment
                countryCode={search.data.countryCode}
                city={search.data.city}
                district={search.data.name}
                product={plan}
                duration={duration}
                promoCode=""
                setSubscriptionModalOpen={handleClose}
                discountedPrice={upgradePrice}
                countryToUnlock={null}
                subscriptionPriceAccordingToUserLocalCurrency={
                  subscriptionPriceAccordingToUserLocalCurrency
                }
                userLocalCurrency={userLocalCurrency}
              />
            </Grid>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};

const stripePromise = loadStripe('pk_live_fzoAY4sM0rn3lvlOO27uuf3q00oSVP9TsO');
const stripePromiseTest = loadStripe('pk_test_dZkzEKTYsZnflrkfbtjGQVz300NSQzQZDS');

const UpgradeYearlyPlanModal = ({ user, setShowUpgradeYearlySubscriptionModal }) => {
  const handleClose = (args: any = null) => {
    setShowUpgradeYearlySubscriptionModal(false);
  };

  const subscriptionPlan = user.subscriptionPlan;

  return (
    <Modal open={true} onClose={handleClose} sx={{ zIndex: 2000 }}>
      <Elements stripe={stripePromise}>
        <StripeContext plan={subscriptionPlan} handleClose={handleClose} />
      </Elements>
    </Modal>
  );
};

export default UpgradeYearlyPlanModal;
